/**
 * buildInputFromCase — produziert ein vollständiges
 * `BuildPlaceholdersInput` für die Stellungnahme-Generierung.
 *
 * Scope:
 *  - caseData / client / business / recipient / orderDate / Termine:
 *    aus DB-Case-Row (RLS-gefiltert, der eingeloggte User sieht nur
 *    seine Cases).
 *  - score + financial: aus den ECHTEN Wizard-Daten (`wizardCase`), im
 *    Zwei-Ebenen-Modell gemergt (Berater-Wert wenn vorhanden, sonst
 *    Mandant — siehe buildScoreResultFromCase). KEIN Rückgriff mehr auf
 *    die Demo-Fixture für diese Blöcke: fehlen die Finanzdaten, wird
 *    `incomplete` geliefert, statt die Mustermann-Werte leaken zu lassen.
 *  - assessments: die fachliche Bewertung des Beraters aus dem Cockpit-Editor
 *    (`customAssessments`). Ist der Editor noch nicht ausgefüllt (null), wird
 *    `incomplete` geliefert — NIE die Demo-Bewertungstexte (Frau Mustermann)
 *    als Fallback, sonst leakten diese in eine formelle Stellungnahme.
 *  - dimensions: bleibt bewusst leer (das Fixture liefert []). Der
 *    Statement-Generator (buildPlaceholders) konsumiert `dimensions` nicht;
 *    sensible Roh-Dimensionsdaten (Art. 9) fließen daher nicht in den Input.
 *
 * Wenn Pflicht-Daten fehlen (clientFullName, bei B2G jobcenterName +
 * jc_file_number, sowie Finanzdaten), liefert die Funktion `incomplete`
 * mit einer Liste der fehlenden Felder. Geschäftsdaten sind nicht
 * Pflicht-Block — fehlende Felder werden durch "nicht erfasst" ersetzt,
 * damit die Demo-Werte (Frau Mustermann) NIE in eine echte
 * Stellungnahme leaken.
 */

import type { statement } from '@tragfaehigkeit/core';
import { getCase, type CaseRow } from '../supabaseCases';
import { getProfile } from '../supabaseUsers';
import type { WizardCaseState } from '../store/wizard';
import { buildScoreResultFromCase, buildFinancialDatasetFromDraft } from '../store/scoreInput';
import {
  DOCUMENT_TYPES,
  listCaseDocuments,
  type CaseDocument,
  type DocumentType,
} from '../supabaseDocuments';

/**
 * Fallback-Briefkopf, falls der eingeloggte Berater sein Profil noch nicht
 * gepflegt hat. Sobald `users.provider_name` gesetzt ist, wird der
 * Fallback durch Profil-Daten ersetzt (siehe `resolveProvider`).
 */
const PROVIDER_DEFAULT: statement.ProviderProfile = {
  name: 'Petschwork Consulting GmbH',
  addressLine1: 'Pettenkoferstr. 4',
  addressLine2: '80336 München',
  phoneEmail: 'Tel. 089/54884586-0 · post@petschwork.de',
  certifications: 'AVGS § 45 SGB III · BAFA-zertifiziert',
  coachName: 'Christian Kopsch',
};

/**
 * Übersetzt das Profil des eingeloggten Beraters in das ProviderProfile-
 * Format des Statement-Cores. Pro Feld einzeln gefallback'd, sodass ein
 * teilweise gepflegtes Profil sich mit den Defaults mischt statt zu kippen.
 *
 * Wird beim Generieren der Stellungnahme aufgerufen — async, weil ein
 * Supabase-Roundtrip nötig ist.
 */
async function resolveProvider(): Promise<statement.ProviderProfile> {
  try {
    const profile = await getProfile();
    if (!profile) return PROVIDER_DEFAULT;
    const fullName = [profile.title, profile.display_name].filter(Boolean).join(' ').trim();
    return {
      name: profile.provider_name ?? PROVIDER_DEFAULT.name,
      addressLine1: profile.provider_address_line1 ?? PROVIDER_DEFAULT.addressLine1,
      addressLine2: profile.provider_address_line2 ?? PROVIDER_DEFAULT.addressLine2,
      phoneEmail: profile.provider_phone_email ?? PROVIDER_DEFAULT.phoneEmail,
      certifications: profile.certifications ?? PROVIDER_DEFAULT.certifications,
      coachName: fullName.length > 0 ? fullName : PROVIDER_DEFAULT.coachName,
    };
  } catch (err) {
    console.warn('resolveProvider: profile lookup failed, using fallback', err);
    return PROVIDER_DEFAULT;
  }
}

export type CaseInputResult =
  | { readonly status: 'ok'; readonly input: statement.BuildPlaceholdersInput }
  | { readonly status: 'incomplete'; readonly missing: ReadonlyArray<string> }
  | { readonly status: 'not_found' };

/**
 * Lädt einen Case aus der DB und überlagert die Demo-Fixture mit den
 * echten Stammdaten + Jobcenter-Daten + Termine.
 */
export async function buildInputFromCase(
  caseId: string,
  customAssessments?: statement.AssessmentTexts | null,
  wizardCase?: WizardCaseState,
  documents?: ReadonlyArray<CaseDocument>,
): Promise<CaseInputResult> {
  const row = await getCase(caseId);
  if (!row) return { status: 'not_found' };

  const missing: string[] = [];
  if (!row.client_full_name) missing.push('Mandanten-Name (Stammdaten)');
  if (row.type === 'b2g') {
    if (!row.jobcenter_name) missing.push('Jobcenter-Name');
    if (!row.jc_file_number) missing.push('Jobcenter-Aktenzeichen');
  }

  // Finanzdaten sind Pflicht für eine formelle Stellungnahme. Ohne echte
  // BWA-Werte (Umsatz/Betriebsausgaben) im Wizard würde sonst der Demo-
  // Datensatz (Frau Mustermann) in ein reales Dokument leaken.
  const financial = wizardCase
    ? buildFinancialDatasetFromDraft(wizardCase.resources, caseId)
    : undefined;
  if (!financial) {
    missing.push('Finanzdaten (BWA: Umsatz & Betriebsausgaben)');
  }

  // Fachliche Bewertung ist Pflicht — ohne sie würden sonst die Demo-
  // Bewertungstexte (Frau Mustermann) in eine reale Stellungnahme leaken.
  if (!customAssessments) {
    missing.push('Fachliche Bewertung (Stellungnahme-Editor unvollständig)');
  }

  if (missing.length > 0) {
    return { status: 'incomplete', missing };
  }

  // Score aus den echten, gemergten Wizard-Daten (nicht aus dem Fixture).
  const score = buildScoreResultFromCase(wizardCase);

  // Echte hochgeladene Unterlagen für die „Geprüfte Unterlagen"-Liste — NIE
  // die Demo-Dokumente. Wenn nicht mitgeliefert, aus der DB laden.
  const docs = documents ?? (await loadDocumentsSafe(caseId));
  const docLists = buildDocumentLists(docs, row.additional_docs);

  const provider = await resolveProvider();
  const today = new Date();

  // Discriminated Union — jeder Branch explizit. `jobcenterId` wird vom
  // Generator nie gelesen (nur caseData.id zählt); wir setzen einen neutralen
  // Wert aus der echten Owner-Id statt eines Demo-Werts.
  const caseData =
    row.type === 'b2g'
      ? ({
          id: row.id as never,
          type: 'b2g' as const,
          legalBasis: row.legal_basis,
          userId: row.owner_id as never,
          status: mapDbStatus(row.status),
          createdAt: new Date(row.created_at),
          updatedAt: new Date(row.updated_at),
          jobcenterId: row.owner_id as never,
          jobcenterFileNumber: row.jc_file_number ?? '',
        } as statement.BuildPlaceholdersInput['caseData'])
      : ({
          id: row.id as never,
          type: 'b2c' as const,
          legalBasis: row.legal_basis,
          userId: row.owner_id as never,
          status: mapDbStatus(row.status),
          createdAt: new Date(row.created_at),
          updatedAt: new Date(row.updated_at),
        } as statement.BuildPlaceholdersInput['caseData']);

  // Verfahrensdaten: optionale Termine nur setzen, wenn erfasst — kein
  // erfundenes „heute minus N Tage". `statementDate` = Ausstellungszeitpunkt.
  const meeting: statement.MeetingMetadata = {
    statementDate: today,
    documentListBwa: docLists.bwa,
    documentListEks: docLists.eks,
    ...(docLists.additional ? { documentListAdditional: docLists.additional } : {}),
    ...(row.data_submitted_at ? { dataSubmissionDate: new Date(row.data_submitted_at) } : {}),
    ...(row.meeting_date ? { meetingDate: new Date(row.meeting_date) } : {}),
    ...(typeof row.meeting_duration_min === 'number'
      ? { meetingDurationMinutes: row.meeting_duration_min }
      : {}),
    ...(row.meeting_format ? { meetingFormat: mapMeetingFormat(row.meeting_format) } : {}),
    ...(row.benefit_start_date ? { benefitStartDate: new Date(row.benefit_start_date) } : {}),
  };

  const input: statement.BuildPlaceholdersInput = {
    caseData,
    // Echte Mandanten-Stammdaten
    client: clientFromRow(row),
    // Echte Geschäftsdaten — Demo-Werte nie in eine reale Stellungnahme leaken.
    business: businessFromRow(row),
    // Roh-Dimensionsdaten (Art. 9) fließen NICHT in den Generator.
    dimensions: [],
    // Echter Auftraggeber
    recipient: recipientFromRow(row),
    meeting,
    // Provider aus Berater-Profil (AP-B3); Fallback auf PROVIDER_DEFAULT
    provider,
    // Echter Score aus den gemergten Wizard-Daten (statt Fixture-62).
    score,
    // Echte Finanzdaten aus dem Resources-Slice (statt Fixture). Cast überbrückt
    // die nominale Brand-Duplikation zwischen Core-Top-Level- und statement-
    // Namespace-Auflösung von FinancialDataset (strukturell identisch).
    financial: financial as statement.BuildPlaceholdersInput['financial'],
    // Fachliche Bewertung des Beraters (oben als Pflicht geprüft → hier gesetzt).
    assessments: customAssessments as statement.AssessmentTexts,
  };

  return { status: 'ok', input };
}

/** Lädt die aktiven Fall-Dokumente; bei Fehler leere Liste (Generierung
 *  läuft weiter, „Geprüfte Unterlagen" zeigt dann „nicht erfasst"). */
async function loadDocumentsSafe(caseId: string): Promise<ReadonlyArray<CaseDocument>> {
  try {
    return await listCaseDocuments(caseId, { onlyActive: true });
  } catch (err) {
    console.warn('buildInputFromCase: listCaseDocuments fehlgeschlagen', err);
    return [];
  }
}

interface DocumentLists {
  readonly bwa: string;
  readonly eks: string;
  readonly additional: string;
}

/**
 * Baut die „Geprüfte Unterlagen"-Zeilen aus den ECHTEN hochgeladenen Dateien.
 * BWA und EKS je eigene Zeile; alle übrigen Belegtypen + Freitext-Zusatz in
 * die Additional-Zeile. Leerer String, wenn kein Dokument dieses Typs vorliegt
 * → renderHtml weist „nicht vorgelegt" aus.
 */
function buildDocumentLists(
  documents: ReadonlyArray<CaseDocument>,
  additionalDocsText: string | null,
): DocumentLists {
  const byType = (t: DocumentType): CaseDocument[] => documents.filter((d) => d.doc_type === t);
  const names = (docs: ReadonlyArray<CaseDocument>): string =>
    docs.map((d) => d.original_filename).join(', ');

  const bwa = names(byType('bwa'));
  const eks = names(byType('eks'));

  const otherTypes: DocumentType[] = ['mietvertrag', 'steuernummer_nachweis', 'sonstiges'];
  const otherParts = otherTypes
    .map((t) => {
      const docs = byType(t);
      return docs.length > 0 ? `${DOCUMENT_TYPES[t].label}: ${names(docs)}` : '';
    })
    .filter((s) => s.length > 0);
  if (additionalDocsText && additionalDocsText.trim().length > 0) {
    otherParts.push(additionalDocsText.trim());
  }

  return { bwa, eks, additional: otherParts.join(' · ') };
}

function clientFromRow(row: CaseRow): statement.ClientStaticData {
  const c: statement.ClientStaticData = {
    fullName: row.client_full_name ?? '',
    ...(row.client_birthdate ? { birthDate: new Date(row.client_birthdate) } : {}),
    ...(row.client_address ? { address: row.client_address } : {}),
  };
  return c;
}

/** Sentinel-String für unausgefüllte Geschäftsfelder. Schützt davor, dass
 *  Demo-Werte (Frau Mustermann) in eine echte Stellungnahme leaken — wenn
 *  ein Berater die Geschäftsdaten nicht erfasst hat, steht im PDF lesbar
 *  "nicht erfasst" und der Berater kann nachpflegen. */
const NOT_RECORDED = 'nicht erfasst';

function businessFromRow(row: CaseRow): statement.BusinessProfile {
  // startDate ist optional: fehlt es, weist das Gutachten „nicht erfasst" aus
  // (kein Sentinel „01.01.1970" mehr).
  return {
    branch: row.business_branch ?? NOT_RECORDED,
    legalForm: row.business_legal_form ?? NOT_RECORDED,
    ...(row.business_start_date ? { startDate: new Date(row.business_start_date) } : {}),
    taxId: row.business_tax_id ?? NOT_RECORDED,
    description: row.business_description ?? NOT_RECORDED,
  };
}

function recipientFromRow(row: CaseRow): statement.RecipientInfo {
  const r: statement.RecipientInfo = {
    ...(row.jobcenter_name ? { jobcenterName: row.jobcenter_name } : {}),
    ...(row.jc_address_line1 ? { jobcenterAddressLine1: row.jc_address_line1 } : {}),
    ...(row.jc_address_line2 ? { jobcenterAddressLine2: row.jc_address_line2 } : {}),
    ...(row.jc_contact_name ? { jobcenterContact: row.jc_contact_name } : {}),
    ...(row.jc_contact_name ? { jobcenterSalutation: row.jc_contact_name } : {}),
    ...(row.jc_file_number ? { jobcenterFileNumber: row.jc_file_number } : {}),
    ...(row.order_date ? { orderDate: new Date(row.order_date) } : {}),
  };
  return r;
}

function mapMeetingFormat(s: string | null): 'Videotermin' | 'Präsenztermin' {
  return s === 'video' ? 'Videotermin' : 'Präsenztermin';
}

/**
 * DB-Status-Enum (in_progress, cancelled) auf Core-Status-Enum
 * (data_collection, archived) mappen. Cores Enum ist feinkörniger;
 * wir nehmen jeweils den nahelegendsten Bucket.
 */
function mapDbStatus(
  s: string,
):
  | 'draft'
  | 'data_collection'
  | 'analysis'
  | 'review'
  | 'statement_pending'
  | 'completed'
  | 'archived' {
  switch (s) {
    case 'draft':
      return 'draft';
    case 'in_progress':
      return 'data_collection';
    case 'review':
      return 'review';
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'archived';
    default:
      return 'draft';
  }
}
